import React, { useState, useEffect } from 'react';
import apiClient from '../../assets/scripts/services/apiClient';

const CompanyViewModal = ({ isOpen, onClose, companyId }) => {
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && companyId) {
      loadCompanyAndUsers();
    }
  }, [isOpen, companyId]);

  const loadCompanyAndUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const [companyData, userData] = await Promise.all([
        apiClient.get(`/organizations/${companyId}`),
        apiClient.get(`/organizations/${companyId}/users`)
      ]);
      setCompany(companyData);
      setUsers(userData || []);
    } catch (err) {
      console.error('Error loading company data:', err);
      setError('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const formatUserType = (userType) => {
    const typeMap = {
      'client_user': 'Client User',
      'client_admin': 'Client Admin',
      'super_user': 'Super User'
    };
    return typeMap[userType] || userType || 'N/A';
  };

  const formatPermissions = (permissions) => {
    if (!permissions || !Array.isArray(permissions)) {
      return 'N/A';
    }
    return permissions.join(', ');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div 
        className="modal-backdrop show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040
        }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="modal show d-block" 
        style={{ zIndex: 1050 }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Company Details</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              />
            </div>
            
            <div className="modal-body">
              {loading ? (
                <div className="text-center p-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              ) : (
                <>
                  {/* Company Details Section */}
                  {company && (
                    <div className="mb-4">
                      <h6 className="mb-3">Company Information</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <strong>Organization Name:</strong>
                            <div>{company.detail?.org_name || 'N/A'}</div>
                          </div>
                          <div className="mb-3">
                            <strong>Organization Type:</strong>
                            <div>{company.detail?.org_type || 'N/A'}</div>
                          </div>
                          <div className="mb-3">
                            <strong>Industry:</strong>
                            <div>{company.detail?.industry || 'N/A'}</div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <strong>Website:</strong>
                            <div>
                              {company.detail?.website ? (
                                <a href={company.detail.website} target="_blank" rel="noopener noreferrer">
                                  {company.detail.website}
                                </a>
                              ) : (
                                'N/A'
                              )}
                            </div>
                          </div>
                          <div className="mb-3">
                            <strong>Phone:</strong>
                            <div>{company.detail?.phone || 'N/A'}</div>
                          </div>
                          <div className="mb-3">
                            <strong>Address:</strong>
                            <div>{company.detail?.address || 'N/A'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Users Section */}
                  <div>
                    <h6 className="mb-3">Associated Users</h6>
                    {users.length === 0 ? (
                      <div className="alert alert-info">
                        No users are currently associated with this company.
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-striped table-bordered">
                          <thead>
                            <tr>
                              <th>User Name</th>
                              <th>Email</th>
                              <th>User Type</th>
                              <th>Role</th>
                              <th>Department</th>
                              <th>Permissions</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map(user => {
                              const userDetail = user.detail || {};
                              const associationDetail = user.association_detail || {};
                              return (
                                <tr key={user.id}>
                                  <td>{userDetail.user_name || 'N/A'}</td>
                                  <td>{userDetail.email || 'N/A'}</td>
                                  <td>{formatUserType(userDetail.user_type)}</td>
                                  <td>{associationDetail.role || 'N/A'}</td>
                                  <td>{associationDetail.department || 'N/A'}</td>
                                  <td>{formatPermissions(associationDetail.permissions)}</td>
                                  <td>
                                    <span className={`badge ${user.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                      {user.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyViewModal;
